/**
 * Workshop Booking - shared pure utilities.
 *
 * Event status/capacity resolution, sold-out capacity modal, and location
 * state-code / place-label helpers. Extracted verbatim from workshop-booking.js
 * so the giant booking script can stay lean. Loaded (defer) BEFORE
 * workshop-booking.js; execution order is guaranteed by defer.
 *
 * Exposes window.WorkshopBookingUtils and keeps the legacy globals
 * window.wbEventBooking + window.wbShowSoldOutCapacityModal (workshop-events-ui.js
 * and other callers depend on those exact names).
 */
(function (global) {
  if (global.WorkshopBookingUtils) return;

  /** Event status from API or DOM dataset (active | sold_out | draft | completed). */
  function wbNormalizeEventStatus(status) {
    if (status == null || status === '') return '';
    return String(status).toLowerCase().replace(/\s/g, '_').replace(/-/g, '_');
  }

  function wbEventCapacityRemaining(evOrCard) {
    if (!evOrCard) return null;
    var raw;
    if (evOrCard.dataset && evOrCard.dataset.capacityRemaining != null && evOrCard.dataset.capacityRemaining !== '') {
      raw = evOrCard.dataset.capacityRemaining;
    } else if (evOrCard.capacity_remaining != null && evOrCard.capacity_remaining !== '') {
      raw = evOrCard.capacity_remaining;
    } else if (evOrCard._wbEv) {
      return wbEventCapacityRemaining(evOrCard._wbEv);
    } else {
      return null;
    }
    var n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }

  function wbGetEventStatus(evOrCard) {
    if (!evOrCard) return '';
    if (evOrCard.dataset && evOrCard.dataset.status != null && evOrCard.dataset.status !== '') {
      return wbNormalizeEventStatus(evOrCard.dataset.status);
    }
    return wbNormalizeEventStatus(evOrCard.status);
  }

  function wbExtractEventStatusRaw(ev) {
    if (!ev || typeof ev !== 'object') return '';
    var candidates = [ev.status, ev.event_status, ev.booking_status];
    if (ev.metaobject && ev.metaobject.status != null) candidates.push(ev.metaobject.status);
    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      if (c == null || c === '') continue;
      if (typeof c === 'object' && c.value != null) c = c.value;
      var n = wbNormalizeEventStatus(c);
      if (n) return n;
    }
    return '';
  }

  function wbEventLookupKeys(ev) {
    if (!ev || typeof ev !== 'object') return [];
    var keys = [];
    ['id', 'event_metaobject_gid', 'event_id', 'gid', 'handle', 'system_id'].forEach(function (k) {
      var v = ev[k];
      if (v == null || v === '') return;
      var s = String(v).trim();
      if (!s) return;
      keys.push(s);
      var num = s.replace(/^gid:\/\/shopify\/Metaobject\//i, '').replace(/\D/g, '');
      if (num && num !== s) keys.push(num);
    });
    return keys;
  }

  function wbBuildEventStatusLookup(events) {
    var map = Object.create(null);
    (Array.isArray(events) ? events : []).forEach(function (ev) {
      var st = wbExtractEventStatusRaw(ev) || wbNormalizeEventStatus(ev.status);
      if (!st) return;
      wbEventLookupKeys(ev).forEach(function (k) {
        map[k] = st;
      });
    });
    return map;
  }

  function wbLookupEventStatus(ev) {
    var map = typeof window !== 'undefined' ? window.__WB_EVENT_STATUS_LOOKUP__ : null;
    if (!map || !ev) return '';
    var keys = wbEventLookupKeys(ev);
    for (var i = 0; i < keys.length; i++) {
      if (map[keys[i]]) return map[keys[i]];
    }
    return '';
  }

  function wbIsDisplayStatusBookable(displayStatus) {
    var d = wbNormalizeEventStatus(displayStatus);
    return d === 'bookable' || d === 'active' || d === 'available' || d === 'open';
  }

  /** Resolve status: API fields, then Liquid metaobject lookup (events-v often omits status). */
  function wbResolveEventStatus(ev) {
    if (!ev || typeof ev !== 'object') return 'active';
    var fromApi = wbExtractEventStatusRaw(ev);
    var fromLiquid = wbLookupEventStatus(ev);
    var displayRaw = wbNormalizeEventStatus(ev.display_status || ev.displayStatus || '');
    var cap = wbEventCapacityRemaining(ev);

    if (fromLiquid === 'sold_out' || fromLiquid === 'draft' || fromLiquid === 'completed') return fromLiquid;
    if (fromApi === 'sold_out' || fromApi === 'draft' || fromApi === 'completed') return fromApi;

    if (wbIsDisplayStatusBookable(displayRaw) && cap != null && cap > 0) {
      return 'active';
    }
    if (fromApi === 'active' || fromApi === 'bookable') return 'active';
    if (fromLiquid) return fromLiquid;
    return 'active';
  }

  /** Sold out: status sold_out OR capacity_remaining <= 0 (shown in list; click -> warning, no sale). */
  function wbIsEventSoldOut(evOrCard) {
    if (!evOrCard) return false;
    var displayRaw = wbNormalizeEventStatus(evOrCard.display_status || evOrCard.displayStatus || '');
    if (displayRaw === 'sold_out') return true;
    if (wbGetEventStatus(evOrCard) === 'sold_out') return true;
    var cap = wbEventCapacityRemaining(evOrCard);
    return cap != null && cap <= 0;
  }

  var WB_SOLD_OUT_CAPACITY_MSG =
    'Sorry, this event has reached capacity. Please select other available options.';

  function ensureSoldOutModalStyles() {
    var style = document.head.querySelector('#wb-sold-out-modal-styles');
    if (!style) {
      style = document.createElement('style');
      style.id = 'wb-sold-out-modal-styles';
      document.head.appendChild(style);
    }
    style.textContent =
      '@keyframes wb-sold-modal-in{from{opacity:0;transform:scale(0.96)}to{opacity:1;transform:scale(1)}}' +
      '.wb-sold-out-modal{position:fixed;inset:0;z-index:2147483020;display:none;align-items:center;justify-content:center;padding:clamp(16px,4vw,28px);box-sizing:border-box;-webkit-tap-highlight-color:transparent;}' +
      '.wb-sold-out-modal.is-open{display:flex;animation:wb-sold-modal-in 0.22s ease-out;}' +
      '.wb-sold-out-modal-backdrop{position:absolute;inset:0;background:rgba(15,18,28,0.55);cursor:pointer;-webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px);}' +
      '.wb-sold-out-modal-dialog{position:relative;z-index:1;width:min(100%,420px);max-width:420px;padding:clamp(1.5rem,4vw,2rem);background:rgb(var(--color-background,255 255 255));color:rgb(var(--color-foreground));border-radius:20px;box-shadow:0 24px 48px rgba(15,23,42,0.18);border:1px solid rgba(var(--color-foreground),0.08);box-sizing:border-box;}' +
      '.wb-sold-out-modal-msg{margin:0 2.5rem 1.25rem;text-align:center;font-size:1rem;line-height:1.55;}' +
      '.wb-sold-out-modal-btn.btn{display:block;width:100%;min-width:0;border-radius:999px;color:var(--colorBtnPrimaryText);background:var(--colorBtnPrimary);border-color:var(--colorBtnPrimary);}' +
      '.wb-sold-out-modal-btn.btn:hover,.wb-sold-out-modal-btn.btn:focus-visible{color:var(--colorBtnPrimaryText);background:var(--colorBtnPrimary);border-color:var(--colorBtnPrimary);}' +
      '.wb-sold-out-modal-x{position:absolute;top:10px;right:10px;width:40px;height:40px;border:0;border-radius:999px;background:rgba(var(--color-foreground),0.08);color:inherit;cursor:pointer;font-size:1.25rem;line-height:1;display:flex;align-items:center;justify-content:center;}';
  }

  function ensureSoldOutCapacityModal() {
    if (document.body._wbSoldOutModal) return document.body._wbSoldOutModal;
    ensureSoldOutModalStyles();
    var wrap = document.createElement('div');
    wrap.className = 'wb-sold-out-modal';
    wrap.setAttribute('hidden', '');
    wrap.innerHTML =
      '<div class="wb-sold-out-modal-backdrop" data-wb-sold-out-modal-dismiss></div>' +
      '<div class="wb-sold-out-modal-dialog" role="alertdialog" aria-modal="true" aria-labelledby="wb-sold-out-modal-title">' +
      '<button type="button" class="wb-sold-out-modal-x" data-wb-sold-out-modal-dismiss aria-label="Close">\u00d7</button>' +
      '<h3 id="wb-sold-out-modal-title" class="wb-sold-out-modal-msg" data-wb-sold-out-modal-msg></h3>' +
      '<button type="button" class="btn wb-sold-out-modal-btn" data-wb-sold-out-modal-dismiss>OK</button>' +
      '</div>';
    document.body.appendChild(wrap);
    document.body._wbSoldOutModal = wrap;

    function closeSoldOutModal() {
      wrap.classList.remove('is-open');
      wrap.setAttribute('hidden', '');
    }
    wrap.querySelectorAll('[data-wb-sold-out-modal-dismiss]').forEach(function (el) {
      el.addEventListener('click', closeSoldOutModal);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && wrap.classList.contains('is-open')) closeSoldOutModal();
    });
    return wrap;
  }

  function showSoldOutCapacityModal(customMsg) {
    var modal = ensureSoldOutCapacityModal();
    var msgEl = modal.querySelector('[data-wb-sold-out-modal-msg]');
    if (msgEl) msgEl.textContent = String(customMsg || WB_SOLD_OUT_CAPACITY_MSG);
    modal.classList.add('is-open');
    modal.removeAttribute('hidden');
    var btn = modal.querySelector('.wb-sold-out-modal-btn');
    if (btn) {
      try {
        btn.focus();
      } catch (_) {}
    }
  }

  /** Bookable: active, not draft/completed/sold_out, capacity >= 1 when known. */
  function wbIsEventBookable(evOrCard) {
    if (!evOrCard) return false;
    if (wbShouldHideEvent(evOrCard)) return false;
    if (wbIsEventSoldOut(evOrCard)) return false;
    var st = wbGetEventStatus(evOrCard);
    if (st && st !== 'active') return false;
    var cap = wbEventCapacityRemaining(evOrCard);
    if (cap != null && cap < 1) return false;
    return true;
  }

  /** Hide from public lists (draft/completed). */
  function wbShouldHideEvent(ev) {
    var st = wbGetEventStatus(ev);
    return st === 'draft' || st === 'completed';
  }

  /** Common state/region long name -> short code so city_key matches backend (e.g. us-nsw-sydney). */
  var STATE_NAME_TO_CODE = {
    'new south wales': 'NSW', 'nsw': 'NSW',
    'victoria': 'VIC', 'vic': 'VIC',
    'queensland': 'QLD', 'qld': 'QLD',
    'western australia': 'WA', 'wa': 'WA',
    'south australia': 'SA', 'sa': 'SA',
    'tasmania': 'TAS', 'tas': 'TAS',
    'australian capital territory': 'ACT', 'act': 'ACT',
    'northern territory': 'NT', 'nt': 'NT'
  };
  function normalizeStateCode(stateName) {
    if (!stateName || stateName.length <= 3) return (stateName || '').toString().trim().toUpperCase();
    var key = stateName.toString().trim().toLowerCase();
    return STATE_NAME_TO_CODE[key] || stateName.toString().trim().toUpperCase().slice(0, 3);
  }

  /** Region metaobject display name -> code (wizard catalog only). */
  var WB_CATALOG_REGION_NAME_TO_CODE = {
    alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA', colorado: 'CO',
    connecticut: 'CT', delaware: 'DE', 'district of columbia': 'DC', florida: 'FL', georgia: 'GA',
    hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA', kansas: 'KS',
    kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD', massachusetts: 'MA',
    michigan: 'MI', minnesota: 'MN', mississippi: 'MS', missouri: 'MO', montana: 'MT',
    nebraska: 'NE', nevada: 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM',
    'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', ohio: 'OH', oklahoma: 'OK',
    oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
    'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT',
    virginia: 'VA', washington: 'WA', 'west virginia': 'WV', wisconsin: 'WI', wyoming: 'WY'
  };

  /** Parse "City, State, Country" or "City, State" from formattedAddress when address_components unavailable. */
  function parseCityStateFromFormattedAddress(formattedAddress) {
    if (!formattedAddress || typeof formattedAddress !== 'string') return null;
    var parts = formattedAddress.split(',').map(function (p) { return p.trim(); }).filter(Boolean);
    if (parts.length < 2) return null;
    var city = parts[0];
    var state = parts[1];
    var country = parts.length >= 3 ? parts[2] : 'US';
    var stateCode = state.length <= 3 ? state.toUpperCase() : normalizeStateCode(state);
    return { city: city, state: state, state_code: stateCode, country: country };
  }

  /**
   * Build display label for a place: prefer formatted_address; else "name, city, state" from address_components.
   * City: locality || postal_town || sublocality_level_1. State: administrative_area_level_1 short_name.
   * Compatible with classic PlaceResult (no fetchFields). Returns fallbackLabel when data missing.
   */
  function getPlaceDisplayLabel(place, fallbackLabel) {
    if (!place) return fallbackLabel || '';
    var fa = (place.formatted_address || place.formattedAddress || '').toString().trim();
    if (fa) return fa;
    var ac = place.address_components || place.addressComponents;
    if (!ac || !Array.isArray(ac)) return fallbackLabel || '';
    var city = '';
    var state = '';
    ac.forEach(function (c) {
      var types = c.types || [];
      var longName = (c.long_name || c.longText || '').toString().trim();
      var shortName = (c.short_name || c.shortText || longName).toString().trim();
      if (types.indexOf('locality') !== -1) city = longName;
      else if ((types.indexOf('postal_town') !== -1 || types.indexOf('sublocality_level_1') !== -1) && !city) city = longName;
      if (types.indexOf('administrative_area_level_1') !== -1) state = shortName || longName;
    });
    var name = (place.name || place.displayName || '').toString().trim() || (fallbackLabel || '').toString().trim();
    var parts = [name, city, state].filter(Boolean);
    return parts.join(', ') || fallbackLabel || '';
  }

  global.WorkshopBookingUtils = {
    normalizeEventStatus: wbNormalizeEventStatus,
    eventCapacityRemaining: wbEventCapacityRemaining,
    getEventStatus: wbGetEventStatus,
    extractEventStatusRaw: wbExtractEventStatusRaw,
    eventLookupKeys: wbEventLookupKeys,
    buildEventStatusLookup: wbBuildEventStatusLookup,
    lookupEventStatus: wbLookupEventStatus,
    isDisplayStatusBookable: wbIsDisplayStatusBookable,
    resolveEventStatus: wbResolveEventStatus,
    isEventSoldOut: wbIsEventSoldOut,
    isEventBookable: wbIsEventBookable,
    shouldHideEvent: wbShouldHideEvent,
    showSoldOutCapacityModal: showSoldOutCapacityModal,
    SOLD_OUT_CAPACITY_MSG: WB_SOLD_OUT_CAPACITY_MSG,
    STATE_NAME_TO_CODE: STATE_NAME_TO_CODE,
    CATALOG_REGION_NAME_TO_CODE: WB_CATALOG_REGION_NAME_TO_CODE,
    normalizeStateCode: normalizeStateCode,
    parseCityStateFromFormattedAddress: parseCityStateFromFormattedAddress,
    getPlaceDisplayLabel: getPlaceDisplayLabel
  };

  // Legacy aliases - keep exact names; workshop-events-ui.js calls
  // window.wbShowSoldOutCapacityModal, and window.wbEventBooking is a documented surface.
  global.wbShowSoldOutCapacityModal = showSoldOutCapacityModal;
  global.wbEventBooking = {
    normalizeStatus: wbNormalizeEventStatus,
    resolveStatus: wbResolveEventStatus,
    capacityRemaining: wbEventCapacityRemaining,
    isSoldOut: wbIsEventSoldOut,
    isBookable: wbIsEventBookable,
    shouldHide: wbShouldHideEvent
  };
})(typeof window !== 'undefined' ? window : this);
